import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { ROUTES } from "~/app/Router";
import Loader from "~/app/common/Loader";
import { route } from "~/app/helpers/route";
import usePageTitle from "~/app/hooks/usePageTitle";
import useTranslation from "~/app/hooks/useTranslation";
import { useAppHeader } from "~/app/modules/appHeader";
import { useStoreActions, useStoreState } from "~/store";
import useWebSocket from "~/app/hooks/useWebSocket";
import List from "./List";
import EmptyMessage from "./EmptyMessage";
import classes from "./style.module.scss";
import Search from "./Search";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNotificationContext } from "~/app/hooks/useNotificationContext";
import Icon from "~/app/common/Icon";
import { MaterialIcons as Icons } from '~/app/common/Icon/icons';
import { CHAT_INBOX_TYPES } from "~/app/data/statuses/chat";
import SearchInput from "~/app/modules/form/Search";
import { API } from "~/app/routes";
import { Project } from "~/types";
import { PROJECTS_ROUTES } from "~/projects/Router";
import type { NotificationPayload } from "~/types";
import MarkAllReadButton from "~/notifications/MarkAllReadButton";

type ChatCategory = keyof typeof CHAT_INBOX_TYPES;

const CATEGORY_CONFIG: { key: ChatCategory; icon: string; titleKey: string; descKey: string }[] = [
    { key: "issues",   icon: "error_outline", titleKey: "tabIssues",   descKey: "descIssues" },
    { key: "service",  icon: "build",         titleKey: "tabService",  descKey: "descService" },
    { key: "projects", icon: "assignment",     titleKey: "tabProjects", descKey: "descProjects" },
    { key: "maskin",   icon: "forum",          titleKey: "tabMaskin",   descKey: "descMaskin" },
];

const Notifications: React.FC = () => {

    const isChatDirect = window.location.pathname.includes('/notifications/chat') || window.location.hash.includes('/notifications/chat');
    const { useNotificationState, useNotificationAction, isChat } = useNotificationContext(isChatDirect);

    const { setData } = useAppHeader();
    const ns = isChat ? "chat" : "notifications";
    const { t } = useTranslation(ns);
    const history = useHistory();
    usePageTitle(t("title"));
    const [chatSection, setChatSection] = useState<ChatCategory | null>(null);

    const fetching = useNotificationState((state) => state.fetching);
    const isSearchOpen = useNotificationState((state) => state.isSearchOpen);
    const notifications = useNotificationState((state) => state.notifications);
    const totalRecords = useNotificationState((state) => state.totalNotificationsCount);
    const notificationsDataFetchAction = useNotificationAction((actions) => actions.fetchNotifications);
    const netWorkStatus = useStoreState((state) => state.app.offline.status);
    const unreadNotificationCount = useStoreState((state) => state.notification.unreadNotificationCount);
    const unreadChatCount = useStoreState((state) => state.chat.unreadNotificationCount);
    const perTypeCount = useStoreState((state) => state.chat.perTypeCount);
    const fetchChatUnreadCounts = useStoreActions((actions) => actions.chat.fetchUnreadNotificationsCount);
    const markAllAsReadAction = useStoreActions((actions) => actions.notification.markAllAsRead);
    const markAllChatAsReadAction = useStoreActions((actions) => actions.chat.markAllAsRead);
    const { updateUnreadCount } = useWebSocket();
    const [isMarkingChatRead, setIsMarkingChatRead] = useState(false);
    const [isMarkingNotificationsRead, setIsMarkingNotificationsRead] = useState(false);
    const notificationPayload = useNotificationState((state) => state.notificationPayload);
    const setNotificationPayload = useNotificationAction((actions) => actions.setNotificationPayload);
    const prevNotificationPayloadRef = useRef<NotificationPayload>(notificationPayload);
    const hasFetchedInitialRef = useRef(false);

    const showCategoryList = isChat && chatSection === null;
    const showNotificationList = isChat && chatSection !== null;

    const currentChatTypeId = chatSection ? CHAT_INBOX_TYPES[chatSection][0] : undefined;
    const currentTypeUnreadCount = currentChatTypeId !== undefined ? (perTypeCount[currentChatTypeId] || 0) : 0;

    useEffect(() => {
        const payload: NotificationPayload = {
            offset: 0,
            searchText: "",
        };
        if (showNotificationList) {
            payload.chatTypes = [...CHAT_INBOX_TYPES[chatSection!]];
        }
        setNotificationPayload(payload);
    }, [showNotificationList, chatSection, setNotificationPayload]);

    useEffect(() => {
        if (showCategoryList) {
            fetchChatUnreadCounts();
        }
    }, [showCategoryList, fetchChatUnreadCounts]);

    useEffect(() => {
        setData({
            title: t("title"),
            subTitle: "",
            backLink: isChat
                ? (showCategoryList ? ROUTES.projects : () => setChatSection(null))
                : (history.length > 1 ? history.goBack : ROUTES.projects),
            showUser: false,
            machineId: "",
            projectId: "",
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: false,
            notification: false,
            search: !showCategoryList,
        });
    }, [setData, t, history, showCategoryList, isChat]);

    const handleMarkAllAsRead = useCallback(async () => {
        if (netWorkStatus === 0 && unreadNotificationCount > 0 && !isMarkingNotificationsRead) {
            setIsMarkingNotificationsRead(true);
            try {
                await markAllAsReadAction();
                updateUnreadCount();
            } finally {
                setIsMarkingNotificationsRead(false);
            }
        }
    }, [netWorkStatus, unreadNotificationCount, isMarkingNotificationsRead, markAllAsReadAction, updateUnreadCount]);

    const handleMarkAllChatAsRead = useCallback(async () => {
        if (netWorkStatus === 0 && unreadChatCount > 0 && !isMarkingChatRead) {
            setIsMarkingChatRead(true);
            try {
                await markAllChatAsReadAction();
                updateUnreadCount();
            } finally {
                setIsMarkingChatRead(false);
            }
        }
    }, [netWorkStatus, unreadChatCount, isMarkingChatRead, markAllChatAsReadAction, updateUnreadCount]);

    const handleMarkChatTypeAsRead = useCallback(async () => {
        if (netWorkStatus === 0 && currentChatTypeId !== undefined && currentTypeUnreadCount > 0 && !isMarkingChatRead) {
            setIsMarkingChatRead(true);
            try {
                await markAllChatAsReadAction({ type: currentChatTypeId });
                updateUnreadCount();
            } finally {
                setIsMarkingChatRead(false);
            }
        }
    }, [netWorkStatus, currentChatTypeId, currentTypeUnreadCount, isMarkingChatRead, markAllChatAsReadAction, updateUnreadCount]);


    const loadNotifications = useCallback((payload: NotificationPayload): void => {
        notificationsDataFetchAction(payload);
    }, [notificationsDataFetchAction]);

    useEffect(() => {
        const prev = prevNotificationPayloadRef.current;
        const curr = notificationPayload;
        const prevTypesJson = JSON.stringify(prev.chatTypes ?? []);
        const currTypesJson = JSON.stringify(curr.chatTypes ?? []);

        if (isChat) {
            if (
                prev.offset !== curr.offset ||
                prev.searchText !== curr.searchText ||
                prevTypesJson !== currTypesJson
            ) {
                loadNotifications(notificationPayload);
                prevNotificationPayloadRef.current = notificationPayload;
            }
            return;
        }

        if (
            prev.offset !== curr.offset ||
            prev.searchText !== curr.searchText
        ) {
            hasFetchedInitialRef.current = true;
            loadNotifications(notificationPayload);
            prevNotificationPayloadRef.current = notificationPayload;
            return;
        }

        if (!hasFetchedInitialRef.current && !fetching) {
            hasFetchedInitialRef.current = true;
            loadNotifications(notificationPayload);
            prevNotificationPayloadRef.current = notificationPayload;
        }
    }, [isChat, loadNotifications, notificationPayload, fetching]);

    const fetchMore = async () => {
        setNotificationPayload({
            ...notificationPayload,
            offset: notifications.length
        });
    };

    if (!isChat) {
        return (
            <>
                {!netWorkStatus && isSearchOpen && <Search />}
                {notifications.length > 0 && unreadNotificationCount > 0 && (
                    <MarkAllReadButton
                        onClick={handleMarkAllAsRead}
                        disabled={isMarkingNotificationsRead}
                    />
                )}
                {fetching ? (
                    <Loader size="32" />
                ) : notifications.length ? (
                    <InfiniteScroll
                        dataLength={notifications.length}
                        next={fetchMore}
                        hasMore={notifications.length < totalRecords}
                        loader={<div className={classes["loader-wrapper"]}><Loader className={classes["loader"]} size="24" /></div>}
                        scrollableTarget="main-scrollable-wrapper"
                    >
                        <List list={notifications} />
                    </InfiniteScroll>
                ) : (
                    <EmptyMessage />
                )}
            </>
        );
    }

    if (showCategoryList) {
        return (
            <div className={classes.categoryList}>
                <div className={classes.chatSubtitleBar}>
                    <span className={classes.chatSubtitleLabel}>{t("subtitle")}</span>
                </div>
                {unreadChatCount > 0 && (
                    <div className={classes.chatUnreadCountWrapper}>
                        <span className={classes.chatSubtitleUnread}>
                            {t("unreadCount", { count: unreadChatCount })}
                        </span>
                        {!netWorkStatus && (
                            <MarkAllReadButton
                                onClick={handleMarkAllChatAsRead}
                                disabled={isMarkingChatRead}
                                inline
                            />
                        )}
                    </div>
                )}
                {CATEGORY_CONFIG.map(({ key, icon, titleKey, descKey }) => {
                    const typeId = CHAT_INBOX_TYPES[key][0];
                    const badgeCount = perTypeCount[typeId] || 0;
                    return (
                        <div
                            key={key}
                            onClick={() => setChatSection(key)}
                            className={classes.categoryRow}
                        >
                            <div className={classes.categoryIcon}>
                                <Icon name={icon as Icons} size={28} />
                            </div>
                            <div className={classes.categoryInfo}>
                                <div className={classes.categoryTitle}>{t(titleKey)}</div>
                                <div className={classes.categoryDesc}>{t(descKey)}</div>
                            </div>
                            <div className={classes.categoryRight}>
                                {badgeCount > 0 && (
                                    <span className={classes.categoryBadge}>
                                        {badgeCount}
                                    </span>
                                )}
                                <Icon name={"chevron_right" as Icons} size={22} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <>
            {chatSection === "projects" && !netWorkStatus && (
                <div className={classes.projectChatToolbar}>
                    <SearchInput<Project>
                        placeholder={t("searchProjectToChat")}
                        className={classes.projectSearchField}
                        inputClassName={classes.projectSearchInput}
                        searchUrl={API.projects}
                        renderItem={(item) => `${item.number} ${item.name}`}
                        onSelect={(item) => history.push(route(PROJECTS_ROUTES.detail, { projectId: item.id }))}
                        iconPosition="left"
                    />
                </div>
            )}
            {!netWorkStatus && isSearchOpen && <Search />}
            {!netWorkStatus && currentTypeUnreadCount > 0 && (
                <MarkAllReadButton
                    onClick={handleMarkChatTypeAsRead}
                    disabled={isMarkingChatRead}
                />
            )}

            {fetching ? (
                <Loader size="32" />
            ) : notifications.length ? (
                <InfiniteScroll
                    dataLength={notifications.length}
                    next={fetchMore}
                    hasMore={notifications.length < totalRecords}
                    loader={<div className={classes["loader-wrapper"]}><Loader className={classes["loader"]} size="24" /></div>}
                    scrollableTarget="main-scrollable-wrapper"
                >
                    <List list={notifications} />
                </InfiniteScroll>
            ) : (
                <EmptyMessage />
            )}
        </>
    );
};

export default Notifications;

